import React, { useEffect, useRef, useState } from 'react';
import { Modal, Alert } from '@mantine/core';
import Icon from 'src/components/common/Icons/Icons/Icon';
import { CustomButton } from 'src/components/common/Buttons/CustomButton';
import EmailModal from './EmailModal';
import FileUploadModal from './FileUploadModal';
import FeedbackModal from './FeedbackModal';
import {
  sendPublicChatMessage,
  createPublicChatSession,
  uploadFileToAzure,
  getPubSubToken,
  getLevels,
  getSubjects,
} from 'src/api/publicChat';
import axios from 'axios';
import {
  WebPubSubClient,
  OnServerDataMessageArgs,
} from '@azure/web-pubsub-client';
import { json } from 'stream/consumers';
import FloatingInput from '../Inputs/FloatingInput';
import { ChatBubble } from '@mui/icons-material';
import {
  ChatBubbleLeftEllipsisIcon,
  UserPlusIcon,
} from '@heroicons/react/24/outline';
interface ChatModalProps {
  opened: boolean;
  onClose: () => void;
  // userName?: string;
  // classLevel?: string;
}

interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  attachment?: {
    file: File;
    previewUrl: string;
    type: string;
    name: string;
  };
}

type Level = {
  id: number;
  name: string;
};

type Subject = {
  id: number;
  name: string;
};

// type ClassLevel = 'Primary 3' | 'Primary 4' | 'Primary 5' | 'Primary 6' | null;

const ChatModal: React.FC<ChatModalProps> = ({ opened, onClose }) => {
  const [userType, setUserType] = useState('Child');
  const [inputText, setInputText] = useState('');
  const [switchToChat, setSwitchToChat] = useState(false);

  // ClassLEvel and Username
  // const [classLevel, setClassLevel] = useState<ClassLevel>('Primary 3');
  const [levels, setLevels] = useState<Level[] | null>(null);
  const [subjects, setSubjects] = useState<Subject[] | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);

  const [userName, setUserName] = useState('');

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [client, setClient] = useState<WebPubSubClient | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [fileUploadComplete, setFileUploadComplete] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const fileUploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [emailModalOpened, setEmailModalOpened] = useState(false);
  const [hasEmailModalOpened, setHasEmailModalOpened] =
    useState<boolean>(false);
  const [conversationId, setConversationId] = useState<string | undefined>(
    undefined
  );
  const [error, setError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<'close' | 'switch' | null>(
    null
  );
  const [pendingUserType, setPendingUserType] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // File upload states
  const [fileUploadModalOpened, setFileUploadModalOpened] = useState(false);
  const [attachedFile, setAttachedFile] = useState<{
    file: File;
    previewUrl: string;
    type: string;
    name: string;
  } | null>(null);

  // Store pending message for file upload completion
  const [pendingMessage, setPendingMessage] = useState<string>('');

  // Sidebar states
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  // Reference for the attachment button
  const attachmentButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const fetchLevels = async () => {
      const levels = await getLevels();
      if (levels) {
        setLevels(levels as Level[]);
      }
    };
    fetchLevels();
  }, []);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (levels && levels.length > 0 && selectedLevel !== null) {
        const subjects = await getSubjects(selectedLevel.id.toString());
        if (subjects) {
          console.log('subjects', subjects);
          setSubjects(subjects as Subject[]);
        }
      }
    };
    fetchSubjects();
  }, [selectedLevel?.id]);

  // Close mobile menu when clicking outside or resizing to desktop
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMobileMenuOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node)
      ) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobileMenuOpen]);

  // API URL
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const PUBSUB_URL = process.env.REACT_APP_PUBSUB_URL;

  // Loading placeholder messages
  const loadingMessages = [
    'Please stand by...',
    'Processing your question...',
    'Thinking hard...',
    'Almost there...',
    'Just a moment...',
    'Working on it...',
    'Analyzing your request...',
    'Getting ready to respond...',
  ];

  const [currentLoadingMessageIndex, setCurrentLoadingMessageIndex] =
    useState(0);
  const [placeholderOpacity, setPlaceholderOpacity] = useState(1);
  const [isComplete, setIsComplete] = useState<boolean>(false);
  const [feedbackModal, setFeedbackModal] = useState<boolean>(false);

  const handleFeedbackClose = () => setFeedbackModal(false);

  // Check if we should open the feedback modal

  ////////////// pubsub client initialization and disconnection useeffect //////////////////////////
  useEffect(() => {
    // Initialize WebPubSub when conversationId is available
    if (conversationId && !client) {
      initializeWebPubSub(conversationId);
    }

    // Cleanup function
    return () => {
      if (client) {
        disconnectWebPubSub();
      }
    };
  }, [conversationId, client]);

  useEffect(() => {
    if (opened) {
      resetAllStates();
    }
  }, [opened]);

  // Cycle through loading messages when isLoading is true
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isLoading) {
      // Start cycling through messages every 3 seconds with transitions
      interval = setInterval(() => {
        // Fade out
        setPlaceholderOpacity(0);

        // After fade out completes, change message and fade back in
        setTimeout(() => {
          setCurrentLoadingMessageIndex(
            (prev) => (prev + 1) % loadingMessages.length
          );
          setPlaceholderOpacity(1);
        }, 300); // 300ms fade out duration
      }, 3000); // 3 seconds between changes
    } else {
      // Reset to first message when not loading
      setCurrentLoadingMessageIndex(0);
      setPlaceholderOpacity(1);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isLoading, loadingMessages.length]);

  // Reset opacity with transition when isLoading state changes
  useEffect(() => {
    if (!isLoading) {
      // Smooth transition back to default placeholder
      setPlaceholderOpacity(0);
      setTimeout(() => {
        setPlaceholderOpacity(1);
      }, 100);
    }
  }, [isLoading]);

  const initializeWebPubSub = async (convId: string) => {
    try {
      if (!convId) return;

      const { token } = await getPubSubToken(convId);
      const fullUrl = `${PUBSUB_URL}${token}`;

      console.log('Initializing WebPubSub with URL:', fullUrl);

      const webPubSubClient = new WebPubSubClient({
        getClientAccessUrl: async () => fullUrl,
      });

      // Set up event handlers
      webPubSubClient.on('connected', (e: any) => {
        console.log('WebPubSub connected:', e);
        setClient(webPubSubClient);
        setIsConnected(true);
      });

      webPubSubClient.on('disconnected', () => {
        console.log('WebPubSub disconnected');
        setIsConnected(false);
      });

      // This event is triggered when the server sends a message directly to the client
      webPubSubClient.on('server-message', (e) => {
        console.log('Server message received:', e);
        console.log('Message structure:', {
          messageType: typeof e.message,
          hasData:
            e.message && typeof e.message === 'object'
              ? 'data' in e.message
              : false,
          dataValue:
            e.message && typeof e.message === 'object' && 'data' in e.message
              ? e.message.data
              : 'none',
        });

        try {
          // Check if we have a valid message with data property
          if (
            e.message &&
            typeof e.message === 'object' &&
            'data' in e.message
          ) {
            // If the message data is "Completed" or "comeplted" (handling typo), handle file upload completion
            const msgData =
              typeof e.message.data === 'string'
                ? e.message.data.toLowerCase()
                : '';

            if (e.message.data === 'Completed') {
              console.log(
                'File upload complete notification received - clearing timeout'
              );

              // Clear any existing timeout
              if (fileUploadTimeoutRef.current) {
                clearTimeout(fileUploadTimeoutRef.current);
                fileUploadTimeoutRef.current = null;
              }

              // Set the state flag
              setFileUploadComplete(true);

              // The direct call to sendChatMessage is handled by useEffect
              // which monitors fileUploadComplete and calls sendChatMessage
            }
            // Check for rate limit errors
            else if (
              msgData.includes('too many requests') ||
              msgData.includes('429')
            ) {
              console.error(
                'Rate limit error received from server:',
                e.message.data
              );
              const errorMsg =
                'Too many requests. Please wait a moment before trying again.';

              setError(errorMsg);

              // Add error message to chat
              const rateErrorMessage: ChatMessage = {
                id: (Date.now() + 1).toString(),
                text: "I'm receiving too many messages right now. Please wait a moment before sending another message.",
                sender: 'ai',
                timestamp: new Date(),
              };

              setMessages((prev) => [...prev, rateErrorMessage]);

              // Clear loading state
              setIsLoading(false);

              // Clear any existing timeout
              if (fileUploadTimeoutRef.current) {
                clearTimeout(fileUploadTimeoutRef.current);
                fileUploadTimeoutRef.current = null;
              }
            }
            // Look for specific error messages
            else {
              // For any other message, display as an error
              const errorMsg =
                typeof e.message.data === 'string'
                  ? e.message.data
                  : 'Error processing your file upload';

              console.error('Received non-completion message:', errorMsg);
              setError(errorMsg);

              // Clear loading state
              setIsLoading(false);

              // Clear any existing timeout
              if (fileUploadTimeoutRef.current) {
                clearTimeout(fileUploadTimeoutRef.current);
                fileUploadTimeoutRef.current = null;
              }
            }
          }
        } catch (error) {
          console.error('Error handling server message:', error);
          setError('An error occurred while processing server message');
          setIsLoading(false);
        }
      });

      // Start the connection
      await webPubSubClient.start();
      console.log('WebPubSub client started');
    } catch (error) {
      console.error('WebPubSub initialization error:', error);
      setError(
        'Failed to establish real-time connection. You can still continue using the chat.'
      );
    }
  };

  const disconnectWebPubSub = () => {
    if (client) {
      console.log('Disconnecting WebPubSub client');
      client.stop();
      setClient(undefined);
      setIsConnected(false);
    }
  };

  // Monitor user message count and show email modal when reaching 10 messages
  useEffect(() => {
    if (isComplete && opened) {
      const timeout = setTimeout(() => {
        onClose(); // Close the chat modal
        if (!hasEmailModalOpened) {
          setEmailModalOpened(true);
          setHasEmailModalOpened(true); // Open the email modal
        }
        // resetAllStates()
      }, 3000); // 3 second delay

      return () => clearTimeout(timeout);
    }
  }, [isComplete, opened, onClose]);

  // Clear Zep session
  const clearZepSession = async (sessionId: string) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/public/chat/clear-session/${sessionId}`
      );
      console.log(`Zep session ${sessionId} cleared successfully`);
    } catch (error) {
      console.error('Error clearing Zep session:', error);
    }
  };

  // Show confirmation if needed, otherwise proceed with action
  const handleActionWithConfirmation = (
    action: 'close' | 'switch',
    newUserType?: string
  ) => {
    if (conversationId) {
      setPendingAction(action);
      if (action === 'switch' && newUserType) {
        setPendingUserType(newUserType);
      }
      setShowConfirmation(true);
    } else {
      if (action === 'close') {
        proceedWithClose();
      } else if (action === 'switch' && newUserType) {
        proceedWithSwitch(newUserType);
      }
    }
  };

  // Proceed with closing after confirmation or direct if no conversation
  const proceedWithClose = async () => {
    if (conversationId) {
      await clearZepSession(conversationId);
    }
    resetAllStates();
    onClose();
  };

  // Proceed with switching after confirmation or direct if no conversation
  const proceedWithSwitch = async (newUserType: string) => {
    if (conversationId) {
      await clearZepSession(conversationId);
    }
    setUserType(newUserType);
    resetStatesExceptUserType(newUserType);
  };

  // Cancel the confirmation
  const cancelConfirmation = () => {
    setShowConfirmation(false);
    setPendingAction(null);
    setPendingUserType(null);
  };

  // Confirm the action
  const confirmAction = async () => {
    setShowConfirmation(false);

    if (pendingAction === 'close') {
      await proceedWithClose();
    } else if (pendingAction === 'switch' && pendingUserType) {
      await proceedWithSwitch(pendingUserType);
    }

    setPendingAction(null);
    setPendingUserType(null);

    //Reset Data
    setSwitchToChat(false);
    setUserName('');
    setSelectedLevel(null);
    setSelectedSubject(null);
  };

  // Reset all states
  const resetAllStates = () => {
    setUserType('Child');
    setSelectedLevel(null);
    setSelectedSubject(null);
    setSwitchToChat(false);
    setUserName('');
    setInputText('');
    setMessages([]);
    setClient(undefined);
    setIsLoading(false);
    setIsConnected(false);
    setFileUploadComplete(false);
    setEmailModalOpened(false);
    setHasEmailModalOpened(false);
    setConversationId(undefined);
    setError(null);
    setPendingAction(null);
    setPendingUserType(null);
    setShowConfirmation(false);
    setFileUploadModalOpened(false);
    setAttachedFile(null);
    setPendingMessage('');
    setIsSidebarCollapsed(false);
    setIsComplete(false);
    setFeedbackModal(false);
  };

  // Reset states except userType
  const resetStatesExceptUserType = (newUserType: string) => {
    // Keep the name and class level from props
    setInputText('');
    setMessages([]);
    setIsLoading(false);
    setConversationId(undefined);
    setError(null);
    setAttachedFile(null);
    setPendingMessage('');
    // New states to reset
    setSwitchToChat(false);
    setClient(undefined);
    setIsConnected(false);
    setFileUploadComplete(false);
    setEmailModalOpened(false);
    setHasEmailModalOpened(false);
    setPendingAction(null);
    setPendingUserType(null);
    setShowConfirmation(false);
    setFileUploadModalOpened(false);
    setIsSidebarCollapsed(false);
    setIsComplete(false);
    setFeedbackModal(false);
  };

  // Reset messages when user type changes
  useEffect(() => {
    // Don't set any initial messages here, as they'll come from the API
    setConversationId(undefined);
  }, [userType]);

  // Scroll to bottom when new messages are added
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    if (!inputText.trim() && !attachedFile) return;

    // Check if still loading from a previous request
    if (isLoading) {
      setError('Please wait for the previous message to be processed.');
      return;
    }

    setIsLoading(true);
    setError(null);

    // Store the message text before clearing input
    const messageText = inputText;
    const messageAttachment = attachedFile;

    // Create a user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: messageText,
      sender: 'user',
      timestamp: new Date(),
      attachment: messageAttachment || undefined,
    };

    // Add user message to state right away for better UX
    setMessages((prev) => [...prev, userMessage]);

    // Clear input field and attachment
    setInputText('');
    setAttachedFile(null);

    try {
      let fileUploaded = false;

      // Handle file upload if there's an attached file
      if (messageAttachment) {
        console.log('Attempting to upload file:', messageAttachment.name);
        // First make sure we have a conversation ID
        if (!conversationId) {
          throw new Error('No conversation ID available for file upload');
        }

        // Store the message for later use when file upload completes
        setPendingMessage(messageText);

        fileUploaded = await uploadFileToAzure(
          messageAttachment.file,
          conversationId
        );

        if (fileUploaded) {
          console.log('File upload successful:', messageAttachment.name);

          // If WebPubSub is connected, wait for the file processing notification
          if (isConnected && client) {
            console.log(
              'WebPubSub is connected, waiting for file processing notification'
            );

            // Set a timeout to prevent hanging if the notification never comes
            fileUploadTimeoutRef.current = setTimeout(() => {
              console.log(
                'File processing timeout reached, proceeding with message'
              );

              // Clear the timeout reference
              fileUploadTimeoutRef.current = null;

              // Send the message despite the timeout
              if (!error) {
                sendChatMessage(messageText, fileUploaded);
              } else {
                // Reset loading state if there's an error
                setIsLoading(false);
                console.log('Not sending message due to error:', error);
              }
            }, 30000);

            // Don't use an interval checker, rely on the useEffect we added
            // which will detect the state change from the WebPubSub message

            return; // Early return to wait for the WebPubSub notification
          }
        } else {
          throw new Error('Failed to upload the file. Please try again.');
        }
      }

      // If no file uploaded or WebPubSub not connected, proceed immediately with the message
      sendChatMessage(messageText, fileUploaded);
    } catch (error) {
      console.error('Error sending message:', error);

      let errorMessage =
        "Sorry, I couldn't process your request. Please try again.";
      let errorNotification =
        'Failed to connect to the chat service. Please try again later.';

      // Handle specific error types
      if (error instanceof Error) {
        // Check for rate limiting errors
        if (error.message.includes('too many requests')) {
          errorMessage =
            "I'm receiving too many messages right now. Please wait a moment before sending another message.";
          errorNotification =
            'Too many requests. Please wait a moment before trying again.';
        } else {
          // Use the error message directly for better context
          errorNotification = error.message;
        }
      }

      // Add error message to the chat
      const aiErrorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: errorMessage,
        sender: 'ai',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiErrorMessage]);

      // Set error message for notification
      setError(errorNotification);
      setIsLoading(false); // Only clear loading on error
    }
  };

  const checkHasEmailModalOpened = async (flag: boolean, complete: boolean) => {
    if (flag) {
      if (!complete) {
        if (!hasEmailModalOpened) {
          setTimeout(() => {
            setHasEmailModalOpened(true);
            setEmailModalOpened(flag);
          }, 3000);
        }
      }
    }
  };

  // Function to send the chat message after file upload (if any)
  const sendChatMessage = async (
    messageText: string,
    fileUploaded: boolean = false
  ) => {
    try {
      // Call the API to send the message
      const apiResponse = await sendPublicChatMessage({
        message: messageText,
        conversation_id: conversationId,
        name: userName,
        level: selectedLevel?.name as string,
        subject: selectedSubject?.name as string,
        is_public: true,
        chat_mode: userType.toLowerCase() as 'parent' | 'child',
        is_new_doc_uploaded: fileUploaded,
      });

      // Update the conversation ID (in case it changed)
      setConversationId(apiResponse.conversation_id);
      checkHasEmailModalOpened(
        apiResponse.should_open_Email,
        apiResponse.is_complete
      );
      // setFeedbackModal(apiResponse.should_open_feedback)

      if (typeof apiResponse.is_complete === 'boolean') {
        setIsComplete(apiResponse.is_complete);
      }

      // Add AI response
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: apiResponse.response,
        sender: 'ai',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending message:', error);

      let errorMessage =
        "Sorry, I couldn't process your request. Please try again.";
      let errorNotification =
        'Failed to connect to the chat service. Please try again later.';

      // Handle specific error types
      if (error instanceof Error) {
        // Check for rate limiting errors
        if (error.message.includes('too many requests')) {
          errorMessage =
            "I'm receiving too many messages right now. Please wait a moment before sending another message.";
          errorNotification =
            'Too many requests. Please wait a moment before trying again.';
        } else {
          // Use the error message from the API for better context
          errorNotification = error.message;

          // For user-friendly errors, show them directly in the chat
          if (
            error.message.includes('wait') ||
            error.message.includes('try again') ||
            error.message.includes('request')
          ) {
            errorMessage = error.message;
          }
        }
      }

      // Add error message
      const errorMessageObj: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: errorMessage,
        sender: 'ai',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessageObj]);

      // Set error message for notification
      setError(errorNotification);
    } finally {
      setIsLoading(false);
      setFileUploadComplete(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle email modal close separately to keep it open when chat modal closes
  const handleEmailModalClose = () => {
    setEmailModalOpened(false);
    // resetAllStates(); // Reset all states including messages
    // onClose();
  };

  // Confirmation Modal Component
  const ConfirmationModal = () => (
    <Modal
      opened={showConfirmation}
      onClose={cancelConfirmation}
      size='sm'
      centered
      withCloseButton={false}
    >
      <div className='p-4 max-h-[100dvh] h-full'>
        <div className='text-center mb-6'>
          <div className='text-amber-500 mb-4'>
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='h-12 w-12 mx-auto'
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
              />
            </svg>
          </div>
          <h3 className='text-lg font-medium text-gray-900 mb-4'>
            Start a new chat?
          </h3>
          <div className='text-gray-600 space-y-2'>
            <p>This will erase your chat, and you won't be able recover it.</p>
            <p>
              Want to save your chats?&nbsp;
              <span
                className='font-bold cursor-pointer underline hover:text-primary'
                onClick={() =>
                  window.open(
                    process.env.REACT_APP_FRONTEND_URL + '/auth/signup',
                    '_blank'
                  )
                }
              >
                Sign up now!
              </span>
            </p>
            <p>
              Tap <span className='font-bold'>Confirm</span> to start over, or{' '}
              <span className='font-bold'>Cancel</span> to go back.
            </p>
          </div>
        </div>

        <div className='flex space-x-3'>
          <button
            onClick={cancelConfirmation}
            className='flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors'
          >
            Cancel
          </button>
          <button
            onClick={confirmAction}
            className='flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors'
          >
            Confirm
          </button>
        </div>
      </div>
    </Modal>
  );

  const handleSwitchToChat = async () => {
    // Validate inputs
    if (!userName || !selectedLevel) {
      setError('Please enter your name and select a class level.');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Call the API to create a new chat session
      const apiResponse = await createPublicChatSession({
        name: userName,
        level: selectedLevel?.name as string,
        subject: selectedSubject?.name as string,
        is_public: true,
        chat_mode: userType.toLowerCase() as 'parent' | 'child',
      });

      // Save the conversation ID for future messages
      setConversationId(apiResponse.conversation_id);

      // Add the initial AI message from the API response
      const aiMessage: ChatMessage = {
        id: '1',
        text: apiResponse.response,
        sender: 'ai',
        timestamp: new Date(),
      };

      // Set the message in state
      setMessages([aiMessage]);

      // Switch to chat view
      setSwitchToChat(true);
    } catch (error) {
      console.error('Error creating chat session:', error);

      let errorMessage = 'Failed to start the chat session. Please try again.';

      // Use the specific error message if available
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLevelSelect = (level?: Level | null) => {
    console.log({ level });
    if (level) {
      setSelectedLevel(level);
    }
  };

  const handleSubjectSelect = (subject?: Subject | null) => {
    console.log({ subject });
    if (subject) {
      setSelectedSubject(subject);
    }
  };

  // Handle file upload
  const handleFileSelect = (file: File) => {
    // Check file size (5MB limit)
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      setError(`File is too large. Maximum size is 5MB.`);
      return;
    }

    // Create a preview URL
    let previewUrl = '';
    let fileType = file.type;

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setAttachedFile({
            file,
            previewUrl: e.target.result as string,
            type: fileType,
            name: file.name,
          });
        }
      };
      reader.readAsDataURL(file);
    } else if (file.type === 'application/pdf') {
      // For PDF files
      setAttachedFile({
        file,
        previewUrl: '',
        type: fileType,
        name: file.name,
      });
    }
  };

  // Remove attached file
  const removeAttachedFile = () => {
    setAttachedFile(null);
  };

  // Open file upload modal
  const openFileUploadModal = () => {
    setFileUploadModalOpened(true);
  };

  // Monitor fileUploadComplete state and trigger message send when it becomes true
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    // Only proceed if fileUploadComplete is true, we have a conversation ID, and there are no errors
    if (fileUploadComplete && conversationId && !error && pendingMessage) {
      console.log(
        'Detected fileUploadComplete changed to true - triggering message send'
      );
      // Small delay to ensure state is fully updated
      timeoutId = setTimeout(() => {
        sendChatMessage(pendingMessage, true);
        setPendingMessage(''); // Clear pending message after sending
      }, 100);
    } else if (fileUploadComplete && error) {
      console.error('File upload completed but with error:', error);
      // Clear loading state if there was an error during file upload completion
      setIsLoading(false);

      // Add error message to the chat for better UX
      const errorMessageObj: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: 'There was an issue processing your file. Your message was sent without the attachment.',
        sender: 'ai',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, errorMessageObj]);

      // Reset fileUploadComplete flag and clear pending message
      setFileUploadComplete(false);
      setPendingMessage('');
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [fileUploadComplete, conversationId, pendingMessage, error]);

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => handleActionWithConfirmation('close')}
        size='100%'
        fullScreen
        padding={0}
        withCloseButton={false}
        styles={{
          overlay: { height: '100dvh' },
          inner: { padding: 0 },
          content: { margin: 0, padding: 0 },
          body: { padding: 0, height: '100%' },
        }}
      >
        <div className='flex max-h-[100dvh] h-full'>
          {/* Mobile Menu Overlay */}
          {isMobileMenuOpen && (
            <div
              className='fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden'
              onClick={() => setIsMobileMenuOpen(false)}
              aria-hidden='true'
            />
          )}

          {/* Sidebar */}
          <div
            ref={sidebarRef}
            className={`fixed md:relative max-h-[100dvh] h-full bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out z-50 ${
              isMobileMenuOpen
                ? 'translate-x-0'
                : '-translate-x-full md:translate-x-0'
            } ${isSidebarCollapsed ? 'md:w-[60px]' : 'w-full md:w-[320px]'}`}
          >
            <div className='flex flex-col h-full'>
              {/* Sidebar Header with Toggle */}
              <div className='px-3 pt-6 pb-4 border-b border-gray-100'>
                <div className='flex items-center justify-between w-full'>
                  <div className='flex items-center gap-3 min-w-0'>
                    {/* Mobile close button */}
                    <button
                      className='md:hidden p-1 -ml-1 mr-1 text-gray-500 hover:text-gray-700'
                      onClick={() => setIsMobileMenuOpen(false)}
                      aria-label='Close menu'
                    >
                      <svg
                        className='w-6 h-6'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M6 18L18 6M6 6l12 12'
                        />
                      </svg>
                    </button>
                    {(!isSidebarCollapsed || window.innerWidth < 768) && (
                      <div className='flex items-center gap-3 min-w-0'>
                        <img
                          src='/images/user-avatar.png'
                          alt='MYAi Brain'
                          className='w-11 h-11 flex-shrink-0'
                        />
                        <h1 className='text-xl font-semibold text-primary truncate min-w-0 max-w-[180px]'>
                          {userName || 'Your Name'}
                        </h1>
                      </div>
                    )}
                  </div>

                  {/* Desktop Toggle Button */}
                  <button
                    onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                    className='hidden md:block p-2 rounded-lg hover:bg-gray-100 transition-colors'
                    title={
                      isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'
                    }
                  >
                    <svg
                      className={`h-5 w-5 text-gray-600 transition-transform duration-200 ${
                        isSidebarCollapsed ? 'rotate-180' : ''
                      }`}
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M15 19l-7-7 7-7'
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Collapsed User Avatar - Only show when collapsed on desktop */}
              {isSidebarCollapsed && window.innerWidth >= 768 && (
                <div className='flex flex-col items-center py-4 border-b border-gray-100'>
                  <img
                    src='/images/user-avatar.png'
                    alt='User Avatar'
                    className='w-8 h-8 rounded-full'
                  />
                </div>
              )}

              {/* Chat History */}
              <div className='flex-1 overflow-y-auto p-3'>
                {/* Chat history list would go here */}
                {(window.innerWidth < 768 || !isSidebarCollapsed) && (
                  <div className='text-center text-gray-500 text-sm py-8'>
                    {/* Chat history coming soon... */}
                  </div>
                )}
              </div>

              {/* Sign Up Button - Only show when expanded */}
              {!isSidebarCollapsed && (
                <div className='border-t border-gray-100 p-3'>
                  {conversationId && (
                    <div className='px-2'>
                      <CustomButton
                        onClick={() => setFeedbackModal(true)}
                        variant='tertiary'
                        size='md'
                        className='w-full'
                      >
                        <span className='text-white text-sm'>
                          Got any Feedback?
                        </span>
                      </CustomButton>
                    </div>
                  )}
                  <div className='px-2 py-3'>
                    <CustomButton
                      onClick={() => {
                        window.open(
                          process.env.REACT_APP_FRONTEND_URL + '/auth/signup',
                          '_blank'
                        );
                      }}
                      variant='default'
                      size='md'
                      width='w-full'
                    >
                      <span className='text-white text-sm'>Sign Up Now!</span>
                    </CustomButton>
                  </div>
                </div>
              )}

              {/* Collapsed Sign Up Icon - Only show when collapsed */}
              {isSidebarCollapsed && (
                <div className='p-3 flex flex-col justify-center'>
                  {conversationId && (
                    <button
                      onClick={() => {
                        setFeedbackModal(true);
                      }}
                      className='p-2 my-3 bg-lime-600 rounded-lg hover:bg-lime-600/85 transition-colors'
                      title='Got any Feedback!'
                    >
                      <ChatBubbleLeftEllipsisIcon className='h-5 w-5 text-white' />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      window.open(
                        process.env.REACT_APP_FRONTEND_URL + '/auth/signup',
                        '_blank'
                      );
                    }}
                    className='p-2 bg-primary rounded-lg hover:bg-primary/90 transition-colors'
                    title='Sign Up Now!'
                  >
                    <UserPlusIcon className='h-5 w-5 text-white' />
                  </button>
                </div>
              )}
            </div>
          </div>
          {/* Main Chat Area */}
          <div className='flex-1 flex flex-col h-full overflow-hidden'>
            {/* Header */}
            <div className='h-16 bg-white border-gray-200 flex items-center justify-between m-1 rounded-lg border relative z-10 px-3 sm:px-6'>
              {/* Mobile Menu Button */}
              <button
                className='md:hidden p-2 -ml-2 mr-2 text-gray-500 hover:text-gray-700 focus:outline-none'
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label='Toggle menu'
              >
                <svg
                  className='w-6 h-6'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M4 6h16M4 12h16M4 18h16'
                  />
                </svg>
              </button>
              <div className='flex items-center gap-2 sm:gap-3 flex-1 min-w-0'>
                <div className='flex items-center gap-1 sm:gap-2 min-w-0 flex-1'>
                  <div className='min-w-0 flex-1'>
                    <h2 className='text-sm sm:text-lg text-gray-800 truncate'>
                      New Session
                    </h2>
                    {selectedLevel && selectedSubject && (
                      <span className='text-xs sm:text-sm text-gray-500 block truncate'>
                        {selectedSubject.name} - {selectedLevel.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className='flex items-center gap-2 sm:gap-4 flex-shrink-0'>
                {/* Custom Switch */}
                <div
                  onClick={() =>
                    handleActionWithConfirmation(
                      'switch',
                      userType === 'Parent' ? 'Child' : 'Parent'
                    )
                  }
                  className='relative w-[75px] h-[24px] sm:w-[90px] sm:h-[28px] bg-[#29748F] rounded-full cursor-pointer flex items-center transition-colors duration-300 shadow-md'
                >
                  <div
                    className={`absolute top-[2px] h-5 w-5 sm:h-6 sm:w-6 rounded-full bg-white shadow-sm transition-all duration-300 transform ${
                      userType === 'Parent'
                        ? 'left-[2px]'
                        : 'left-[52px] sm:left-[62px]'
                    }`}
                  />
                  <div className='relative z-10 w-full text-[9px] sm:text-[11px] font-medium text-center select-none text-white'>
                    {userType === 'Parent' ? 'Parent' : 'Child'}
                  </div>
                </div>

                <button
                  className='text-[#29748F] hover:text-[#1d5c73] transition-colors'
                  onClick={() => handleActionWithConfirmation('close')}
                >
                  <svg
                    className='h-5 w-5 sm:h-6 sm:w-6'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M6 18L18 6M6 6l12 12'
                    />
                  </svg>
                </button>
              </div>
            </div>

            <div
              className='flex-1 relative mx-1 mb-1 rounded-lg border border-gray-200 bg-white h-full overflow-hidden'
              style={{
                backgroundImage: 'url(/images/doodles.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                zIndex: '20',
              }}
            >
              {!switchToChat ? (
                <div className='flex flex-col justify-center items-center p-4 sm:p-8 h-full'>
                  {/* Logo */}
                  <div className='pb-4'>
                    <img
                      src='/images/favicon.png'
                      alt='MYAi Brain'
                      className='w-14 h-14 md:w-16 md:h-16'
                    />
                  </div>

                  {/* Show loading spinner if API call is in progress */}
                  {isLoading ? (
                    <div className='flex flex-col items-center justify-center p-4 sm:p-8'>
                      <div className='w-12 h-12 sm:w-16 sm:h-16 border-t-4 border-b-4 border-primary rounded-full animate-spin mb-4'></div>
                      <p className='text-base sm:text-lg text-gray-600 text-center'>
                        Starting your session...
                      </p>
                    </div>
                  ) : (
                    <div className='w-full flex flex-col items-center justify-center gap-3 sm:gap-4 max-w-md mx-auto'>
                      {/* Input for name */}
                      <FloatingInput
                        value={userName}
                        setValue={setUserName}
                        label='Full Name'
                        maxLength={30}
                        width='w-full'
                        placeholder='Enter your full name'
                      />
                      {/* Options for Primary User */}
                      <h2 className='text-primary text-base sm:text-lg font-bold text-center'>
                        Choose Your Level
                      </h2>

                      <div className='grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 w-full'>
                        {levels?.map((level) => (
                          <CustomButton
                            key={level.id}
                            onClick={() => handleLevelSelect(level)}
                            variant={
                              level.id === selectedLevel?.id
                                ? 'default'
                                : 'outline'
                            }
                            size='sm'
                            width='w-full'
                            className='min-h-[40px] sm:min-h-[44px]'
                            textClass='text-xs sm:text-sm'
                          >
                            {level.name}
                          </CustomButton>
                        ))}
                      </div>

                      {selectedLevel && (
                        <>
                          <h2 className='text-primary text-base sm:text-lg font-bold text-center'>
                            Choose Your Subject
                          </h2>
                          <div className='grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 w-full'>
                            {subjects?.map((subject) => (
                              <CustomButton
                                key={subject.id}
                                onClick={() => handleSubjectSelect(subject)}
                                variant={
                                  selectedSubject &&
                                  subject.id === selectedSubject?.id
                                    ? 'default'
                                    : 'outline'
                                }
                                size='sm'
                                width='w-full'
                                className='min-h-[40px] sm:min-h-[44px]'
                                textClass='text-xs sm:text-sm'
                              >
                                {subject.name}
                              </CustomButton>
                            ))}
                          </div>
                        </>
                      )}

                      <CustomButton
                        onClick={handleSwitchToChat}
                        variant='default'
                        size='md'
                        width='w-full sm:w-3/4 md:w-1/2'
                        disabled={
                          !(
                            userName !== '' &&
                            selectedLevel &&
                            selectedSubject
                          ) || isLoading
                        }
                        className='mt-2 min-h-[44px] sm:min-h-[48px]'
                      >
                        <span className='px-4 sm:px-8'>
                          {isLoading ? (
                            <div className='flex items-center justify-center'>
                              <div className='w-4 h-4 sm:w-5 sm:h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2'></div>
                              Starting...
                            </div>
                          ) : (
                            'Next'
                          )}
                        </span>
                      </CustomButton>

                      {/* Error display */}
                      {error && (
                        <div className='text-red-500 text-center text-sm px-4'>
                          {error}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className='flex flex-col max-w-4xl xl:max-w-6xl h-full mx-auto'>
                  {/* Error notification */}
                  {error && (
                    <div className='absolute top-2 left-2 right-2 sm:top-4 sm:left-1/2 sm:right-auto sm:transform sm:-translate-x-1/2 z-50 w-auto sm:w-full sm:max-w-md'>
                      <Alert
                        color='red'
                        title='Error'
                        withCloseButton
                        onClose={() => setError(null)}
                        className='text-sm'
                      >
                        {error}
                      </Alert>
                    </div>
                  )}

                  {/* Messages Container */}
                  <div className='h-full flex flex-col space-y-3 sm:space-y-4 overflow-y-auto py-3 sm:py-6 px-2 md:px-6'>
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`flex ${
                          message.sender === 'ai' ? 'sm:pr-[48px]' : ''
                        } ${
                          message.sender === 'user'
                            ? 'justify-end'
                            : 'justify-start'
                        }`}
                      >
                        {message.sender === 'ai' && (
                          <img
                            src='/images/favicon.png'
                            alt='MYAi Brain'
                            className='w-8 h-8 sm:w-11 sm:h-11 mr-2 sm:mr-3 my-auto flex-shrink-0'
                          />
                        )}
                        <div
                          className={`${
                            message.sender === 'user'
                              ? 'bg-[#29748F] text-white max-w-[85%] sm:max-w-[70%]'
                              : 'bg-white border max-w-[85%] sm:max-w-[70%]'
                          } rounded-lg p-2 sm:p-3 my-1 break-words text-sm sm:text-base`}
                        >
                          {/* Show attachment if present */}
                          {message.attachment &&
                            message.attachment.type.startsWith('image/') && (
                              <div className='mb-2'>
                                <img
                                  src={message.attachment.previewUrl}
                                  alt='Attachment'
                                  className='max-w-full rounded-lg max-h-40 sm:max-h-60 object-contain'
                                />
                              </div>
                            )}

                          {/* Show file icon for PDF attachments */}
                          {message.attachment &&
                            message.attachment.type === 'application/pdf' && (
                              <div className='flex items-center bg-gray-100 p-2 rounded-lg mb-2'>
                                <div className='bg-red-100 p-1 sm:p-2 rounded-md mr-2 flex-shrink-0'>
                                  <svg
                                    className='h-4 w-4 sm:h-5 sm:w-5 text-red-500'
                                    fill='none'
                                    stroke='currentColor'
                                    viewBox='0 0 24 24'
                                  >
                                    <path
                                      strokeLinecap='round'
                                      strokeLinejoin='round'
                                      strokeWidth={2}
                                      d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                                    />
                                  </svg>
                                </div>
                                <div className='flex flex-col min-w-0'>
                                  <span className='font-medium text-xs sm:text-sm text-gray-800 truncate'>
                                    {message.attachment.name}
                                  </span>
                                  <span className='text-xs text-gray-500'>
                                    PDF Document
                                  </span>
                                </div>
                              </div>
                            )}
                          <div>{message.text}</div>
                        </div>
                        {message.sender === 'user' && (
                          <img
                            src='/images/user-avatar.png'
                            alt='User Avatar'
                            className='w-8 h-8 sm:w-11 sm:h-11 ml-2 sm:ml-3 my-auto flex-shrink-0'
                          />
                        )}
                      </div>
                    ))}
                    {isLoading && (
                      <div className='flex items-start space-x-2 sm:space-x-3'>
                        <img
                          src='/images/favicon.png'
                          alt='MYAi Brain'
                          className='w-8 h-8 sm:w-11 sm:h-11 flex-shrink-0'
                        />
                        <div className='bg-white text-sm rounded-lg p-3 sm:p-4 max-w-[80%] border'>
                          <div className='flex items-center space-x-1'>
                            <div className='w-2 h-2 sm:w-2.5 sm:h-2.5 bg-gray-400 rounded-full animate-bounce' />
                            <div
                              className='w-2 h-2 sm:w-2.5 sm:h-2.5 bg-gray-400 rounded-full animate-bounce'
                              style={{ animationDelay: '0.15s' }}
                            />
                            <div
                              className='w-2 h-2 sm:w-2.5 sm:h-2.5 bg-gray-400 rounded-full animate-bounce'
                              style={{ animationDelay: '0.3s' }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {/* Message Input - Fixed at Bottom */}
                  <div className='sticky bottom-0 w-full bg-transparent flex flex-col justify-center px-2 md:px-6'>
                    {/* Attached file display */}
                    {attachedFile && (
                      <div className='flex w-full mx-auto mb-2'>
                        <div className='bg-gray-100 rounded-lg p-1 flex items-center gap-2 shadow-sm max-w-full'>
                          {attachedFile.type.startsWith('image/') ? (
                            <img
                              src={attachedFile.previewUrl}
                              alt='Attached file'
                              className='h-6 w-6 sm:h-8 sm:w-8 object-cover rounded flex-shrink-0'
                            />
                          ) : attachedFile.type === 'application/pdf' ? (
                            <div className='bg-red-100 p-1 rounded-md flex-shrink-0'>
                              <svg
                                className='h-5 w-5 sm:h-6 sm:w-6 text-red-500'
                                fill='none'
                                stroke='currentColor'
                                viewBox='0 0 24 24'
                              >
                                <path
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                  strokeWidth={2}
                                  d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                                />
                              </svg>
                            </div>
                          ) : (
                            <svg
                              className='h-6 w-6 sm:h-8 sm:w-8 text-gray-500 flex-shrink-0'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                              />
                            </svg>
                          )}
                          <span className='text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[180px]'>
                            {attachedFile.name}
                          </span>
                          <button
                            onClick={removeAttachedFile}
                            className='text-gray-500 hover:text-gray-700 ml-1 sm:ml-2 flex-shrink-0'
                          >
                            <svg
                              className='h-4 w-4 sm:h-5 sm:w-5'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M6 18L18 6M6 6l12 12'
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}

                    <div className='flex w-full mx-auto relative gap-2'>
                      <div className='flex-1 bg-white rounded-lg sm:rounded-xl border border-gray-200'>
                        <div className='relative'>
                          {/* File Upload Modal - positioned absolutely relative to input area */}
                          <FileUploadModal
                            opened={fileUploadModalOpened}
                            onClose={() => setFileUploadModalOpened(false)}
                            onConfirm={handleFileSelect}
                            buttonRef={attachmentButtonRef}
                          />

                          <div className='relative w-full'>
                            <input
                              type='text'
                              className='w-full border-0 rounded-lg sm:rounded-2xl px-3 sm:px-6 py-2.5 sm:py-3 focus:outline-none focus:ring-2 focus:ring-[#29748F] bg-white pr-10 sm:pr-12 text-sm sm:text-base'
                              value={inputText}
                              onChange={(e) => setInputText(e.target.value)}
                              onKeyPress={handleKeyPress}
                              disabled={isLoading}
                            />
                            {/* Custom animated placeholder */}
                            {!inputText && (
                              <div
                                className={`absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 pointer-events-none ${
                                  isLoading ? 'text-gray-400' : 'text-gray-500'
                                } text-sm sm:text-base transition-opacity duration-300 ease-in-out`}
                                style={{
                                  opacity: placeholderOpacity,
                                }}
                              >
                                {isLoading
                                  ? loadingMessages[currentLoadingMessageIndex]
                                  : 'Ask Your Question'}
                              </div>
                            )}
                          </div>
                          <button
                            ref={attachmentButtonRef}
                            className='absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#29748F] transition-colors p-1'
                            onClick={openFileUploadModal}
                            disabled={isLoading}
                          >
                            <svg
                              className='h-4 w-4 sm:h-5 sm:w-5'
                              fill='none'
                              stroke='currentColor'
                              viewBox='0 0 24 24'
                            >
                              <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.414a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13'
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                      <button
                        className={`${
                          isLoading || !inputText.trim()
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-[#29748F] hover:bg-[#1d5c73]'
                        } text-white p-2.5 sm:p-3 rounded-lg transition-colors flex-shrink-0`}
                        onClick={handleSendMessage}
                        disabled={isLoading || !inputText.trim()}
                      >
                        <svg
                          className='h-4 w-4 sm:h-5 sm:w-5'
                          fill='none'
                          stroke='currentColor'
                          viewBox='0 0 24 24'
                        >
                          <path
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            strokeWidth={2}
                            d='M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5'
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Disclaimer */}
                  <div className="w-full pb-2 pt-1 px-4 sm:px-10 text-xxs xs:text-xs text-gray-400 text-center">
                    <div className="max-w-full">
                      Disclaimer: I'm an AI bot! I try my best, but I can make
                      mistakes. Reach out to us at{" "}
                      <span className="whitespace-nowrap">
                        supp&#111;rt&#64;myaic&#111;nsult&#46;com
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <EmailModal
        opened={emailModalOpened}
        onClose={handleEmailModalClose}
        zepSessionId={conversationId ? conversationId : ''}
        name={userName}
      />

      {/* Confirmation Modal */}
      <FeedbackModal
        opened={feedbackModal}
        onClose={handleFeedbackClose}
        previousEmail=''
        name={userName}
        conversation_id={conversationId ? conversationId : ''}
      />
      <ConfirmationModal />
    </>
  );
};

export default ChatModal;
